import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { getStylists, createStylist, archiveStylist } from '../services/stylistService';
import { updateStaffStatus } from '../services/profileService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Stack,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import StylistProfileSettings from '../components/profile/StylistProfileSettings';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CampaignIcon from '@mui/icons-material/Campaign';
import ArchiveIcon from '@mui/icons-material/Archive';
import type { Stylist } from '@app-types/models/stylist';
import { PageContainer, PageHeader, SectionCard, EmptyState, ResponsiveTable } from '../components/ui';
import type { Column } from '../components/ui';

interface ListingAction {
  title?: string;
  message?: string;
}

interface StoredUser {
  _id: string;
  email?: string;
  [key: string]: unknown;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
      requirements?: string[];
    };
  };
}

const StylistPageNew: React.FC = () => {
  const { t } = useTranslation();
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [selectedStylist, setSelectedStylist] = useState<Stylist | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [requirements, setRequirements] = useState<string[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showCreateStylistFromBusinessAccountDialog, setShowCreateStylistFromBusinessAccountDialog] = useState<boolean>(false);
  const [showListingDialog, setShowListingDialog] = useState<boolean>(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState<boolean>(false);
  const [listingAction, setListingAction] = useState<ListingAction>({});
  const [loading, setLoading] = useState<boolean>(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchStylists = useCallback(async (): Promise<void> => {
    try {
      const result = await getStylists();
      setStylists(Array.isArray(result) ? result : []);
    } catch {
      setError(t('stylistPage.errorLoadingStylists'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStylists();
  }, [fetchStylists]);

  const sendMagicLink = async (email: string): Promise<boolean> => {
    try {
      const businessId = (JSON.parse(localStorage.getItem('user')!) as StoredUser)._id;
      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/stylist-signup-page?businessId=${businessId}&email=${email}`
        }
      });
      if (error) throw error;
      return true;
    } catch (error: unknown) {
      console.error('Error sending magic link:', error);
      throw error;
    }
  };

  const handleInvite = async (): Promise<void> => {
    try {
      setError('');
      const emailExists = stylists.some(stylist => stylist.email === inviteEmail);
      if (emailExists) {
        setError(t('stylistPage.emailExists'));
        return;
      }

      await sendMagicLink(inviteEmail);
      await createStylist({ email: inviteEmail });
      toast.success(t('stylistPage.invitationSent'));
      setInviteEmail('');
      fetchStylists();
    } catch (error: unknown) {
      console.error('Failed to send invitation:', error);
      setError(t('stylistPage.invitationFailed'));
    }
  };

  const handleViewDetails = (stylist: Stylist): void => {
    setSelectedStylist(stylist);
    setShowModal(true);
  };

  const handleListing = (stylist: Stylist): void => {
    setSelectedStylist(stylist);
    setError('');
    setRequirements([]);
    const isListing = stylist.status === 'listing';
    setListingAction({
      title: isListing ? t('stylistPage.stopListingConfirmationTitle') : t('stylistPage.listingConfirmationTitle'),
      message: isListing ? t('stylistPage.stopListingConfirmationMessage') : t('stylistPage.listingConfirmationMessage'),
    });
    setShowListingDialog(true);
  };

  const handleConfirmListingChange = async (): Promise<void> => {
    try {
      const newStatus = selectedStylist!.status === 'listing' ? 'not_listing' : 'listing';
      await updateStaffStatus({ staffId: selectedStylist!._id, status: newStatus });
      toast.success(newStatus === 'listing' ? t('stylistPage.listingSuccess') : t('stylistPage.stopListingSuccess'));
      setError('');
      setRequirements([]);
      fetchStylists();
    } catch (error: unknown) {
      console.error('Failed to update listing status:', error);
      const apiError = error as ApiError;

      setError(`${t('stylistPage.listingFailed')} ${apiError.response?.data?.message ?? ''}`);

      if (apiError.response?.data?.requirements) {
        setRequirements(apiError.response.data.requirements);
      }
    } finally {
      setShowListingDialog(false);
    }
  };

  const handleArchiveStylist = (stylist: Stylist): void => {
    setSelectedStylist(stylist);
    setShowArchiveDialog(true);
  };

  const handleConfirmArchiveStylist = async (): Promise<void> => {
    try {
      await archiveStylist(selectedStylist!._id);
      toast.success(t('stylistPage.archiveSuccess', 'Stylist archived successfully.'));
      fetchStylists();
    } catch (error: unknown) {
      console.error('Failed to archive stylist:', error);
      setError(t('stylistPage.archiveFailed', 'Failed to archive stylist.'));
    } finally {
      setShowArchiveDialog(false);
      setSelectedStylist(null);
    }
  };

  const handleCloseModal = (): void => {
    setShowModal(false);
    setSelectedStylist(null);
  };

  const handleCloseListingDialog = (): void => {
    setShowListingDialog(false);
    setSelectedStylist(null);
    setError('');
    setRequirements([]);
  };

  const handleCloseArchiveDialog = (): void => {
    setShowArchiveDialog(false);
    setSelectedStylist(null);
  };

  const handleCreateStylistFromBusinessAccount = (): void => {
    setShowCreateStylistFromBusinessAccountDialog(true);
    setError('');
    setRequirements([]);
  };

  const handleConfirmCreateStylistFromBusinessAccount = async (): Promise<void> => {
    try {
      const user: StoredUser = JSON.parse(localStorage.getItem('user')!);
      if (!user || !user.email) {
        setError(t('stylistPage.errorNoUserEmail', 'Could not find user email. Please log in again.'));
        return;
      }
      await createStylist({ email: user.email });
      toast.success(t('stylistPage.createSuccess'));
      fetchStylists();
    } catch (error: unknown) {
      console.error('Failed to create stylist:', error);
      const apiError = error as ApiError;
      setError(`${t('stylistPage.createFailed')} ${apiError.response?.data?.message ?? ''}`);
    } finally {
      handleCloseCreateStylistFromBusinessAccountDialog();
    }
  };

  const handleCloseCreateStylistFromBusinessAccountDialog = (): void => {
    setShowCreateStylistFromBusinessAccountDialog(false);
  };

  // --- Presentation helpers ---
  const renderStatusChip = (status?: string) => {
    const s = (status ?? '').toLowerCase();
    const isListing = s === 'listing';
    const isArchivedOrSuspended = s === 'archived' || s === 'suspended';
    return (
      <Chip
        label={status || '—'}
        size="small"
        color={isListing ? 'success' : isArchivedOrSuspended ? 'warning' : 'default'}
        variant={isListing || isArchivedOrSuspended ? 'filled' : 'outlined'}
        sx={{ fontWeight: 600, textTransform: 'capitalize' }}
      />
    );
  };

  const renderStylistActions = (stylist: Stylist, fullWidth = false) => (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<VisibilityIcon />}
        onClick={() => handleViewDetails(stylist)}
        fullWidth={fullWidth}
        sx={{ minHeight: 40 }}
      >
        {t('stylistPage.viewDetails')}
      </Button>
      <Button
        size="small"
        variant="contained"
        startIcon={<CampaignIcon />}
        onClick={() => handleListing(stylist)}
        fullWidth={fullWidth}
        sx={{ minHeight: 40 }}
      >
        {stylist.status === 'listing' ? t('stylistPage.stopListing') : t('stylistPage.listing')}
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="warning"
        startIcon={<ArchiveIcon />}
        onClick={() => handleArchiveStylist(stylist)}
        fullWidth={fullWidth}
        sx={{ minHeight: 40 }}
      >
        {t('stylistPage.archive', 'Archive')}
      </Button>
    </>
  );

  const columns: Column<Stylist>[] = [
    {
      key: 'name',
      header: t('stylistPage.tableHeaders.name'),
      primary: true,
      render: (row) => row.displayName || '—',
    },
    {
      key: 'email',
      header: t('stylistPage.tableHeaders.email'),
      render: (row) => row.email || '—',
    },
    {
      key: 'role',
      header: t('stylistPage.tableHeaders.role'),
      render: (row) => row.role || '—',
    },
    {
      key: 'status',
      header: t('stylistPage.tableHeaders.status'),
      render: (row) => renderStatusChip(row.status),
    },
    {
      key: 'actions',
      header: t('stylistPage.tableHeaders.actions'),
      align: 'right',
      hideOnMobile: true, // actions render via renderMobileActions on phones
      render: (row) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexWrap: 'wrap' }}>
          {renderStylistActions(row)}
        </Stack>
      ),
    },
  ];

  const inviteAction = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
      <TextField
        type="email"
        placeholder={t('stylistPage.invitePlaceholder')}
        value={inviteEmail}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteEmail(e.target.value)}
        fullWidth
        size="small"
        sx={{ flexGrow: 1 }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flexShrink: 0 }}>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleInvite}
          fullWidth={isMobile}
          sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
        >
          {t('stylistPage.inviteButton')}
        </Button>
        <Button
          variant="outlined"
          onClick={handleCreateStylistFromBusinessAccount}
          fullWidth={isMobile}
          sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
        >
          {t('stylistPage.useSameEmail')}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <PageContainer>
      <PageHeader
        title={t('dashboard.manageStylists')}
        subtitle={t('stylistPage.subtitle', 'Invite stylists, manage their listing status and details.')}
        icon={<PeopleIcon />}
        classicPath="/classic/stylist"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {requirements.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {requirements.map((req, index) => (
            <div key={index}>{req}</div>
          ))}
        </Alert>
      )}

      <SectionCard
        title={t('stylistPage.inviteTitle', 'Invite a stylist')}
        subtitle={t('stylistPage.inviteSubtitle', 'Send an invitation link or add a stylist using your own email.')}
        icon={<PersonAddIcon />}
        sx={{ mb: 2.5 }}
      >
        {inviteAction}
      </SectionCard>

      <SectionCard
        title={t('stylistPage.listTitle', 'Your stylists')}
        icon={<PeopleIcon />}
        disableContentPadding
        contentSx={{ p: { xs: 1.5, sm: 2 } }}
      >
        <ResponsiveTable<Stylist>
          columns={columns}
          rows={stylists}
          loading={loading}
          getRowKey={(row) => row._id}
          renderMobileActions={(row) => (
            <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
              {renderStylistActions(row, true)}
            </Stack>
          )}
          emptyState={
            <EmptyState
              dense
              icon={<PeopleIcon />}
              title={t('stylistPage.noStylistsTitle', 'No stylists yet')}
              description={t('stylistPage.noStylistsDescription', 'Invite your first stylist to start building your team.')}
              action={
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleCreateStylistFromBusinessAccount}
                  sx={{ minHeight: 44 }}
                >
                  {t('stylistPage.useSameEmail')}
                </Button>
              }
            />
          }
        />
      </SectionCard>

      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t('stylistPage.editStylist')}
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {selectedStylist && <StylistProfileSettings stylist={selectedStylist} fetchStylists={fetchStylists} onClose={handleCloseModal}/>}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCreateStylistFromBusinessAccountDialog}
        onClose={handleCloseCreateStylistFromBusinessAccountDialog}
      >
        <DialogTitle>{t('stylistPage.useSameEmail')}</DialogTitle>
        <DialogContent>
          <p>{t('stylistPage.confirmCreateStylist')}</p>
          <Button onClick={handleCloseCreateStylistFromBusinessAccountDialog}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmCreateStylistFromBusinessAccount}>
            {t('common.confirm')}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showArchiveDialog}
        onClose={handleCloseArchiveDialog}
      >
        <DialogTitle>{t('stylistPage.confirmArchiveTitle', 'Confirm Archive')}</DialogTitle>
        <DialogContent>
          <p>
            {t('stylistPage.confirmArchiveMessage', 'Are you sure you want to archive this stylist? They will be hidden from view.')}
            {selectedStylist && (
              <>
                {' '}
                <strong>{selectedStylist.displayName || selectedStylist.email}</strong>
              </>
            )}
          </p>
          <Button onClick={handleCloseArchiveDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirmArchiveStylist} color="warning">
            {t('common.confirm', 'Confirm')}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showListingDialog}
        onClose={handleCloseListingDialog}
      >
        <DialogTitle>{listingAction.title}</DialogTitle>
        <DialogContent>
          <p>
            {listingAction.message}
            {selectedStylist && (
              <>
                {' '}
                <strong>{selectedStylist.displayName || selectedStylist.email}</strong>
              </>
            )}
          </p>
          <Button onClick={handleCloseListingDialog}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmListingChange}>
            {t('common.confirm')}
          </Button>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default StylistPageNew;
